'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import Image from 'next/image';


interface CreateMenuItemProps {
  title: string;
  image: string;
  href: string;
  description?: string;
  disabled?: boolean;
  badge?: string;
}

export function CreateMenuItem({
  title,
  image,
  href,
  description,
  disabled = false,
  badge,
}: CreateMenuItemProps) {
  const [imgError, setImgError] = useState(false);

  const content = (
    <div
      className={[
        'group flex min-h-[82px] w-full items-center gap-4',
        'border-b border-slate-700/70 px-4 py-3',
        'transition-all duration-200',
        disabled
          ? 'cursor-not-allowed opacity-50' :'cursor-pointer hover:bg-slate-800/80 active:scale-[0.99]',
      ].join(' ')}
    >
      <div className="relative h-14 w-14 shrink-0 overflow-hidden rounded-xl bg-slate-800/60">
        <Image
          src={imgError ? '/assets/images/no_image.png' : image}
          alt={`${title} realistic icon`}
          fill
          sizes="56px"
          className="object-contain drop-shadow-md transition-transform duration-200 group-hover:scale-105"
          onError={() => setImgError(true)}
        />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <span className="truncate text-base font-medium text-white">
            {title}
          </span>
          {badge && (
            <span className="rounded-full bg-emerald-500/15 px-2 py-0.5 text-xs font-medium text-emerald-300">
              {badge}
            </span>
          )}
        </div>
        {description && (
          <p className="mt-1 line-clamp-1 text-sm text-slate-400">
            {description}
          </p>
        )}
      </div>

      <svg className="w-4 h-4 text-slate-500 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
      </svg>
    </div>
  );

  if (disabled) return content;

  return (
    <Link href={href} aria-label={title}>
      {content}
    </Link>
  );
}

const createMenuItems: CreateMenuItemProps[] = [
  {
    title: 'Sell Product',
    image: '/assets/images/sell-product.png',
    href: '/create-listing',
    description: 'List any product for sale',
  },
  {
    title: 'Sell Vehicle',
    image: '/assets/images/sell-vehicle.png',
    href: '/create-listing',
    description: 'Cars, motorbikes, trucks',
  },
  {
    title: 'Post Property',
    image: '/assets/images/post-property.png',
    href: '/create-listing',
    description: 'Houses, apartments, land',
  },
  {
    title: 'Post Job',
    image: '/assets/images/post-job.png',
    href: '/create-listing',
    description: 'Hire talent or post vacancies',
  },
  {
    title: 'List Hotel',
    image: '/assets/images/list-hotel.png',
    href: '/create-listing',
    description: 'Rooms, lodges, guesthouses',
  },
  {
    title: 'Add Food Item',
    image: '/assets/images/add-food.png',
    href: '/create-listing',
    description: 'Restaurant meals & delivery',
  },
  {
    title: 'Add Pharmacy',
    image: '/assets/images/add-pharmacy.png',
    href: '/create-listing',
    description: 'Medicines & health products',
  },
  {
    title: 'Create Event',
    image: '/assets/images/create-event.png',
    href: '/create-listing',
    description: 'Concerts, meetups, workshops',
  },
  {
    title: 'Add Transport',
    image: '/assets/images/add-transport.png',
    href: '/create-listing',
    description: 'Buses, trucks, logistics',
  },
  {
    title: 'Upload Video',
    image: '/assets/images/upload-video.png',
    href: '/create-listing',
    description: 'Share video content',
  },
  {
    title: 'Offer Service',
    image: '/assets/images/offer-service.png',
    href: '/create-listing',
    description: 'Plumbing, cleaning, repairs',
  },
  {
    title: 'Publish News',
    image: '/assets/images/publish-news.png',
    href: '/create-listing',
    description: 'Articles, press releases',
  },
];

interface CreateMenuProps {
  onClose?: () => void;
}

export function CreateMenu({ onClose }: CreateMenuProps) {
  return (
    <section className="mx-auto w-full max-w-md px-3 py-4">
      <div className="overflow-hidden rounded-[28px] border border-slate-700 bg-[#111b31] shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-4 border-b border-slate-700/70">
          <div>
            <h2 className="text-white font-bold text-lg leading-tight">Create & Post</h2>
            <p className="text-slate-400 text-xs mt-0.5">Choose what you want to list</p>
          </div>
          {onClose && (
            <button
              onClick={onClose}
              className="w-8 h-8 rounded-full bg-slate-700/60 flex items-center justify-center hover:bg-slate-600 transition-colors"
              aria-label="Close menu"
            >
              <svg className="w-4 h-4 text-slate-300" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
        </div>
        {/* Items */}
        {createMenuItems.map((item, idx) => (
          <div key={item.title} onClick={onClose} className={idx === createMenuItems.length - 1 ? '[&>a>div]:border-b-0 [&>div]:border-b-0' : ''}>
            <CreateMenuItem {...item} />
          </div>
        ))}
      </div>
    </section>
  );
}

export default CreateMenu;
